/**
 * NotificationWidget Hook - Orchestrates mediator + store
 * Fetches notifications on mount, provides actions for component
 */

import { useEffect, useCallback } from 'react';
import { logger, mediator } from '@/application/core';
import { GetTopNotificationsQuery, type TopNotificationsResponse } from '@/application/queries/notifications';
import {
  useNotifications,
  useNewNotificationCount,
  useNotificationLoading,
  useNotificationError,
  useHasBeenOpened,
  useBadgeCount,
  useSetNotificationData,
  useSetNotificationLoading,
  useSetNotificationError,
  useMarkNotificationsOpened,
} from './NotificationWidget.store';

export function useNotificationWidget() {
  const notifications = useNotifications();
  const newCount = useNewNotificationCount();
  const loading = useNotificationLoading();
  const error = useNotificationError();
  const hasBeenOpened = useHasBeenOpened();
  const badgeCount = useBadgeCount();
  const setData = useSetNotificationData();
  const setLoading = useSetNotificationLoading();
  const setError = useSetNotificationError();
  const markAsOpenedAction = useMarkNotificationsOpened();

  const fetchNotifications = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const result = await mediator.send<TopNotificationsResponse>(new GetTopNotificationsQuery());
      setData(result.notifications, result.newCount);
    } catch (err) {
      logger.error('NotificationWidget', 'Error fetching notifications:', err);
      setError(err instanceof Error ? err : new Error('Failed to fetch notifications'));
    }
  }, [setData, setLoading, setError]);

  // Fetch on mount
  useEffect(() => {
    let mounted = true;

    async function init() {
      setError(null);
      setLoading(true);
      try {
        const result = await mediator.send<TopNotificationsResponse>(new GetTopNotificationsQuery());
        if (mounted) {
          setData(result.notifications, result.newCount);
        }
      } catch (err) {
        logger.error('NotificationWidget', 'Error fetching notifications:', err);
        if (mounted) {
          setError(err instanceof Error ? err : new Error('Failed to fetch notifications'));
        }
      }
    }

    init();
    return () => {
      mounted = false;
    };
  }, []); // Run once on mount

  const markAsOpened = useCallback(() => {
    markAsOpenedAction();
  }, [markAsOpenedAction]);

  return {
    notifications,
    newCount,
    badgeCount,
    loading,
    error,
    hasBeenOpened,
    markAsOpened,
    refresh: fetchNotifications,
  };
}
